<template>
  <div>
    <div class="headline-medium text-start flex justify-between items-center">
      Ditt företag
      <div
        v-if="!useFetchFlow"
        class="headline-small underline"
        :class="{
          'text-primary-default': !darkText,
          'text-black': darkText,
        }"
        @click="setFetchFlow"
      >
        Hämta företag med orgnummer
      </div>
    </div>
    <div v-if="lookupError" class="base text-dark pt-16">
      {{ lookupError }}
    </div>
    <div v-if="useFetchFlow">
      <form class="flex relative pt-16">
        <ion-input
          v-model="orgNumber"
          mode="md"
          :label="'Orgnummer'"
          label-placement="floating"
          fill="outline"
          color="primary"
          placeholder="Skriv..."
          class="mr-[-8px]"
          inputmode="numeric"
          :maxlength="12"
          :clear-input="true"
          @keydown.enter="(e) => e.preventDefault()"
        />
        <button
          class="bg-primary-default h-56 px-16 text-sm font-medium mt-auto z-10 rounded-r-md"
          :class="{
            'text-white': !darkText,
            'text-black': darkText,
          }"
          @click="getCompanies"
        >
          <div v-if="!loadingCompanies">Hämta</div>
          <ion-spinner
            v-else
            name="dots"
            color="light"
            class="mt-4"
          />
        </button>
      </form>
    </div>
    <div v-else-if="!selectedWorkplace">
      <form>
        <TextInput
          class="mt-16"
          :value="orgNumber"
          :use-debounce="true"
          :placeholder="'Orgnummer'"
          inputmode="numeric"
          :maxlength="12"
          :invalid="attemptedSubmit && !orgNumber"
          @on-input="(input) => orgNumber = input"
        />
        <TextInput
          class="mt-16"
          :value="manualName"
          :use-debounce="true"
          :placeholder="'Bolag'"
          :invalid="attemptedSubmit && !manualName"
          @on-input="(input) => manualName = input"
        />
        <TextInput
          class="mt-16"
          :value="manualAddress"
          :use-debounce="true"
          :placeholder="'Adress'"
          :invalid="attemptedSubmit && !manualAddress"
          @on-input="(input) => manualAddress = input"
        />
        <div class="flex mt-16">
          <TextInput
            class="w-1/3"
            :value="manualZip"
            :use-debounce="true"
            :placeholder="'Postnummer'"
            :invalid="attemptedSubmit && !manualZip"
            @on-input="(input) => manualZip =input"
          />
          <TextInput
            class="flex-1 ml-16"
            :value="manualCity"
            :use-debounce="true"
            :placeholder="'Postort'"
            :invalid="attemptedSubmit && !manualCity"
            @on-input="(input) => manualCity = input"
          />
        </div>
      </form>
      <SelectInput
        class="mt-16"
        :select-label="'Bransch'"
        :is-optional="true"
        :options="sectorsOptions"
        :default-value="selectedSector"
        @change="selectSector"
      />
      <Button
        class="mt-16"
        :text="'Använd företag'"
        :type="'primary'"
        @click="submitManually"
      />
    </div>
    <div v-if="selectedWorkplace">
      <div class="py-32 px-24 bg-peach flex justify-between items-start mt-24 rounded-md">
        <div class="text-start w-1/2">
          <div class="headline-small mb-12">
            {{ companyName }}
          </div>
          <div class="text-dark text-sm">
            {{ selectedWorkplace.Gatuadress }},
          </div>
          <div class="text-dark text-sm">
            {{ `${selectedWorkplace.Postnummer} ${selectedWorkplace.Postort}` }}
          </div>
        </div>
        <button
          class="font-bold text-sm underline"
          :class="{
            'text-primary-default': !darkText,
            'text-black': darkText,
          }"
          @click="changeWorkplace"
        >
          Ändra
        </button>
      </div>
      <SelectInput
        class="mt-24"
        :select-label="'Bransch'"
        :is-optional="true"
        :options="sectorsOptions"
        :default-value="selectedSector"
        @change="selectSector"
      />
    </div>
    <Modal
      :label="companyName || 'Arbetsplatser'"
      :open="modalOpen"
      :breakpoints="[0, 1]"
      :initial-breakpoint="1"
      @close="modalOpen = false"
    >
      <div>
        <div class="px-16 border-b border-light pb-24">
          <TextInput
            class="mt-16"
            placeholder="Sök på adress eller butiksid"
            :is-search-input="true"
            :value="searchString"
            @on-input="searchWorkplaces"
          />
          <div
            class="mt-16 headline-small underline"
            :class="{
              'text-primary-default': !darkText,
              'text-black': darkText,
            }"
            @click="enableManualInput"
          >
            Hittar du inte ditt arbetsställe i listan?
          </div>
        </div>
        <div v-if="companyList.length > 0 || !loadingCompanies" class="px-16 pb-88 overflow-auto">
          <RadioInput :options="companiesOptionsList" @on-input="selectCompanyWorkplace" />
        </div>
      </div>
    </Modal>
  </div>
</template>

<script lang="ts" setup>
import { IonSpinner, IonInput } from '@ionic/vue';
import { LookupResponse, Workplace } from '@/constants/types/api';
import { computed, ref, watch } from 'vue';
import { useFilterStore } from '@/store/filters';
import useApi from '@/composeables/useApi';
import Modal from '@/components/Modal.vue';
import TextInput from '@/components/form-elements/TextInput.vue';
import RadioInput from '@/components/form-elements/RadioInput.vue';
import SelectInput from '@/components/form-elements/SelectInput.vue';
import Button from '@/components/Button.vue';
import { useUserStore } from '@/store/user';
import { storeToRefs } from 'pinia';

const userStore = useUserStore();
const { darkText } = storeToRefs(userStore);

const loadingCompanies = ref(false);
const companyName = ref('');
const companyList = ref<Workplace[]>([]);

const modalOpen = ref(false);
const useFetchFlow = ref(true);
const orgNumber = ref('');
const searchString = ref('');
const selectedWorkplace = ref<Workplace | null>(null);
const selectedSector = ref<number | null>(null);

const lookupError = ref('');

const manualName = ref('');
const manualAddress = ref('');
const manualZip = ref('');
const manualCity = ref('');
const attemptedSubmit = ref(false);

const emit = defineEmits<{
  (e: 'setWorkplace', payload: Workplace): void;
  (e: 'setCompany', payload: { name: string, orgNr: string }): void;
  (e: 'setSector', payload: number): void;
}>();

const props = defineProps<{
  company?: { name: string; org_nr: string };
  workplace?: Workplace;
}>();

const filterStore = useFilterStore();
const { apiGet } = useApi();

const selectCompanyWorkplace = (input: string) => {
  const workplace = companyList.value.find(c => `${c.Postort}, ${c.Gatuadress}` === input) as Workplace;
  selectedWorkplace.value = workplace;
  emit('setWorkplace', workplace);
};
const getCompanies = async(e?) => {
  e?.preventDefault();
  loadingCompanies.value = true;
  const company = await apiGet<LookupResponse>(`/companies/lookup?org_nr=${orgNumber.value}`);

  if (company) {
    companyList.value = company.Arbetsstalle;
    companyName.value = company.JuridisktNamn;
    manualName.value = company.JuridisktNamn;
    emit('setCompany', { name: company.JuridisktNamn, orgNr: orgNumber.value });
    modalOpen.value = true;
  } else {
    useFetchFlow.value = false;
    lookupError.value = 'Kunde inte hitta ditt företag, var vänlig fyll i uppgifterna manuellt';
    selectedWorkplace.value = null;
  }
  loadingCompanies.value = false;
};

const changeWorkplace = async() => {
  if (useFetchFlow.value) {
    if (companyList.value.length === 0) {
      await getCompanies();
    } else {
      modalOpen.value = true;
    }
  } else {
    selectedWorkplace.value = null;
  }
};

const companiesOptionsList = computed(() => {
  return companyList.value
    .filter(c => {
      if (!searchString.value) return true;
      const term = searchString.value.toLowerCase();
      const street = (c.Gatuadress ?? '').toLowerCase();
      const city = (c.Postort ?? '').toLowerCase();
      const workplaceId = c.ArbetsstalleID != null ? String(c.ArbetsstalleID).toLowerCase() : '';
      return street.includes(term)
        || city.includes(term)
        || workplaceId.includes(term);
    })
    .map(c => ({
      id: c.ArbetsstalleID,
      value: `${c.Postort}, ${c.Gatuadress}`,
    }));
});

const searchWorkplaces = (input: string) => {
  searchString.value = input;
};

const sectorsOptions = computed(() => {
  return filterStore.sectors().map(sector => ({ value: sector.id, label: sector.nicename }));
});

const selectSector = (value: number | string) => {
  selectedSector.value = value as number;
  emit('setSector', value as number);
};

const enableManualInput = () => {
  selectedWorkplace.value = null;
  useFetchFlow.value = false;
  modalOpen.value = false;
};

const submitManually = () => {
  attemptedSubmit.value = true;
  if (
    !manualName.value ||
    !manualAddress.value ||
    !manualZip.value ||
    !manualCity.value
  ) {
    return;
  }

  companyName.value = manualName.value;

  emit('setCompany', {
    name: manualName.value,
    orgNr: orgNumber.value,
  });

  selectedWorkplace.value = {
    Namn: manualName.value,
    Gatuadress: manualAddress.value,
    Postnummer: manualZip.value,
    Postort: manualCity.value,
  };

  emit('setWorkplace', {
    Namn: manualName.value,
    Gatuadress: manualAddress.value,
    Postnummer: manualZip.value,
    Postort: manualCity.value,
  });
};

const setFetchFlow = () => {
  lookupError.value = '';
  useFetchFlow.value = true;
};

watch(
  () => props.company,
  () => {
    companyName.value = props.company.name;
    orgNumber.value = props.company.org_nr;
  }
);

watch(
  () => props.workplace,
  () => {
    selectedWorkplace.value = props.workplace;
  }
);
</script>
